import { Injectable, inject } from '@angular/core';
import { Either, Left, Right } from 'purify-ts';
import { environment } from '../../../../environments/environment';
import { SessionSignalsStateService } from '../../../application/session/session-store.service';
import { resetAuthInterceptorState } from '../../../auth.interceptor';
import { SessionRepositoryFailure } from '../../../domain/session/failure';
import { ISessionRepository } from '../../../domain/session/i-session-repository';
import { SsoRedirectQuery, SsoStart } from '../../../domain/session/types';
import { IStorageRepository } from '../../../domain/storage';
import { ApiResponse } from '../../../util/type';
import { IHttpClient } from '../../services/httpClient/interface';
import { rightValue } from '../../../util/either';
import { isDevEnvironment } from '../../../util/environment';

@Injectable({
  providedIn: 'root',
})
export class SessionRepository implements ISessionRepository {
  private httpClient = inject(IHttpClient);
  private sessionStore = inject(SessionSignalsStateService);
  private storage = inject(IStorageRepository);

  public async signInWithSSO(): Promise<Either<SessionRepositoryFailure, SsoStart>> {
    try {
      const { body } = await this.httpClient.post<
        ApiResponse<{ authCodeUrlResponse: string; verifier: string }>
      >('/ad-auth/login', {
        redirectUrl: `${environment.redirectAdBaseUrl}/auth/redirect`,
      });

      const { authCodeUrlResponse, verifier } = body.data;

      this.storage.setStorage('verifier', verifier);

      return Right({ providerUrl: authCodeUrlResponse, verifierCode: verifier });
    } catch (error) {
      return Left(
        SessionRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível iniciar o login.',
        })
      );
    }
  }

  public async exchangeSSOToken(
    queryObject: SsoRedirectQuery
  ): Promise<Either<SessionRepositoryFailure, void>> {
    const storedVerifier = this.storage.getStorage('verifier');

    if (storedVerifier.isLeft()) {
      return Left(SessionRepositoryFailure.missingVerifier());
    }

    try {
      const { body } = await this.httpClient.post<
        ApiResponse<{ token: string; refreshToken: string }>
      >('/ad-auth/token', { ...queryObject, verifier: rightValue(storedVerifier) });

      this.storage.deleteFromStorage('verifier');
      this.persistSession(body.data.token, body.data.refreshToken);
      this.persistUserId(queryObject['client_info']);

      return Right(undefined);
    } catch (error) {
      return Left(
        SessionRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível concluir o login.',
        })
      );
    }
  }

  public async refreshToken(): Promise<Either<SessionRepositoryFailure, string>> {
    try {
      const stored = this.storage.getStorage('refreshToken');
      const refreshToken = stored.isRight() ? rightValue(stored) : '';

      const { body } = await this.httpClient.post<
        ApiResponse<{ token: string; refreshToken: string }>
      >('/ad-auth/refresh-token', { refreshToken });

      this.persistSession(body.data.token, body.data.refreshToken);

      return Right(body.data.token);
    } catch (error) {
      return Left(
        SessionRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível renovar a sessão.',
        })
      );
    }
  }

  public async signOut(): Promise<Either<SessionRepositoryFailure, { url: string }>> {
    let urlToRedirect = '/login';

    if (isDevEnvironment()) {
      this.clearSession();

      return Right({ url: urlToRedirect });
    }

    const response = await this.httpClient
      .post<ApiResponse<string | { logoutUrl: string }>>('/ad-auth/logout', {
        logoutUrl: `${environment.redirectAdBaseUrl}/login`,
      })
      .catch(() => null);

    if (response) {
      const { body } = response;

      urlToRedirect =
        typeof body.data === 'string' ? body.data : (body.data?.logoutUrl ?? urlToRedirect);
    }

    this.clearSession();

    return Right({ url: urlToRedirect });
  }

  public async fakeSignIn(): Promise<Either<SessionRepositoryFailure, void>> {
    try {
      const { body } = await this.httpClient.post<
        ApiResponse<{ token: string; refreshToken: string }>
      >('/ad-auth/token', {});

      this.storage.deleteFromStorage('verifier');
      this.persistSession(body.data.token, body.data.refreshToken);

      return Right(undefined);
    } catch (error) {
      return Left(
        SessionRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível entrar.',
        })
      );
    }
  }

  private persistSession(token: string, refreshToken: string): void {
    resetAuthInterceptorState();

    this.storage.setStorage('token', token);
    this.storage.setStorage('refreshToken', refreshToken);
    this.storage.setStorage('isLogged', true);
    this.sessionStore.set('token', token);
    this.sessionStore.set('isLogged', true);
  }

  private persistUserId(clientInfo: string | undefined): void {
    const { uid } = this.parseClientInfo(clientInfo);

    this.storage.setStorage('userId', uid);
    this.sessionStore.set('userId', uid);
  }

  private clearSession(): void {
    (['token', 'refreshToken', 'verifier', 'isLogged', 'userId'] as const).forEach(key =>
      this.storage.deleteFromStorage(key)
    );

    this.sessionStore.setState({ token: '', isLogged: false, userId: '' });
  }

  private parseClientInfo(clientInfo: string | undefined): { uid: string } {
    if (!clientInfo) {
      return { uid: '' };
    }

    try {
      const base64 = clientInfo.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '==='.slice((base64.length + 3) % 4);
      const json = decodeURIComponent(
        window
          .atob(padded)
          .split('')
          .map(char => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      );

      return { uid: JSON.parse(json)?.uid ?? '' };
    } catch {
      return { uid: '' };
    }
  }
}
